import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type FormEvent,
  type ReactNode,
} from "react";
import {
  ArrowLeft,
  ArrowRight,
  ChevronLeft,
  ChevronRight,
  Home,
  Monitor,
  RotateCw,
  Smartphone,
} from "lucide-react";
import {
  BRAND_SITE_PAGE_TREE_ID,
  BrandSitePageTree,
} from "@/components/brand-site/BrandSitePageTree";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Popover, PopoverAnchor, PopoverContent } from "@/components/ui/popover";
import type { BrandSiteReleasePage } from "@/lib/brand-site-api";
import { addressBarTreeQuery } from "@/lib/brand-site-page-tree";
import {
  homeSlugFromPages,
  injectPreviewBridge,
  isPreviewNavMessage,
  missingPreviewPageHtml,
  parsePreviewHref,
  pathForPreviewSlug,
} from "@/lib/brand-site-preview-nav";
import { cn } from "@/lib/utils";

type Viewport = "desktop" | "mobile";
type NavState = { stack: string[]; index: number };

type CompareToggle = {
  side: "current" | "target";
  onSideChange: (side: "current" | "target") => void;
  currentLabel: string;
  targetLabel: string;
};

type ConfirmAction = {
  label: string;
  confirming?: boolean;
  /** destructive：危险操作（回滚等） */
  tone?: "default" | "destructive";
  onConfirm: () => void | Promise<void>;
};

/** 正式版列表内快速切版（上一版=更旧，下一版=更新） */
type VersionNav = {
  hasPrevious: boolean;
  hasNext: boolean;
  onPrevious: () => void;
  onNext: () => void;
};

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: ReactNode;
  description?: ReactNode;
  pages: BrandSiteReleasePage[] | null;
  loading?: boolean;
  error?: string | null;
  /** 回滚确认：切换看「当前正式版 / 目标版」 */
  compareToggle?: CompareToggle;
  /** 发布页预览：标题旁上一版 / 下一版 */
  versionNav?: VersionNav;
  confirm?: ConfirmAction;
};

/** 正式版预览 / 回滚对照：全屏，展示方式对齐设计页 LivePreview */
export function BrandSiteReleasePreviewDialog({
  open,
  onOpenChange,
  title,
  description,
  pages,
  loading = false,
  error = null,
  compareToggle,
  versionNav,
  confirm,
}: Props) {
  const [viewport, setViewport] = useState<Viewport>("desktop");
  const [nav, setNav] = useState<NavState>({ stack: ["index"], index: 0 });
  const [missingPath, setMissingPath] = useState<string | null>(null);
  const [reloadToken, setReloadToken] = useState(0);
  const [urlDraft, setUrlDraft] = useState("/");
  const [treeOpen, setTreeOpen] = useState(false);
  const urlInputRef = useRef<HTMLInputElement>(null);
  const wasOpenRef = useRef(false);

  const orderedPages = useMemo(() => {
    if (!pages?.length) return [];
    return [...pages].sort((a, b) => {
      if (a.is_home !== b.is_home) return a.is_home ? -1 : 1;
      return a.slug.localeCompare(b.slug, "zh-CN");
    });
  }, [pages]);

  const treePages = useMemo(
    () =>
      orderedPages.map((page, index) => ({
        id: index + 1,
        slug: page.slug,
        title: page.title || page.slug,
        is_home: page.is_home,
      })),
    [orderedPages],
  );

  const homeSlug = useMemo(() => homeSlugFromPages(treePages), [treePages]);

  const viewingSlug = nav.stack[nav.index] ?? homeSlug;
  const viewingPage = orderedPages.find((page) => page.slug === viewingSlug) ?? null;
  const pageHtml = viewingPage?.html?.trim() ? viewingPage.html : "";
  const rawHtml = missingPath ? missingPreviewPageHtml(missingPath) : pageHtml;
  const srcDoc = useMemo(() => injectPreviewBridge(rawHtml, { pick: false }), [rawHtml]);
  const previewIdentityKey = `${compareToggle?.side ?? "solo"}:${viewingSlug}:${reloadToken}`;
  const displayPath = missingPath ?? pathForPreviewSlug(viewingSlug, homeSlug);

  useEffect(() => {
    if (!open) {
      wasOpenRef.current = false;
      setViewport("desktop");
      setMissingPath(null);
      setReloadToken(0);
      setTreeOpen(false);
      setUrlDraft("/");
      setNav({ stack: ["index"], index: 0 });
      return;
    }

    const justOpened = !wasOpenRef.current;
    wasOpenRef.current = true;
    const home = orderedPages.find((page) => page.is_home) ?? orderedPages[0];
    const homeOrFallback = home?.slug ?? homeSlug;

    if (justOpened) {
      setNav({ stack: [homeOrFallback], index: 0 });
      setMissingPath(null);
      setReloadToken(0);
      setTreeOpen(false);
      return;
    }

    // 切正式版加载中 pages 会短暂为空：不要把导航打回首页
    if (orderedPages.length === 0) return;

    // 切正式版 / 对照侧：尽量停在同路径
    setNav((current) => {
      const slug = current.stack[current.index];
      if (slug && orderedPages.some((page) => page.slug === slug)) return current;
      return { stack: [homeOrFallback], index: 0 };
    });
    setMissingPath(null);
    setTreeOpen(false);
  }, [open, compareToggle?.side, orderedPages, homeSlug]);

  useEffect(() => {
    setUrlDraft(displayPath);
  }, [displayPath]);

  const pushSlug = useCallback((slug: string) => {
    setNav((current) => {
      if (current.stack[current.index] === slug) return current;
      const stack = [...current.stack.slice(0, current.index + 1), slug];
      return { stack, index: stack.length - 1 };
    });
  }, []);

  const navigateTo = useCallback(
    (slug: string, opts?: { index?: number }) => {
      const page = orderedPages.find((item) => item.slug === slug);
      if (opts?.index != null) {
        setNav((current) => ({ ...current, index: opts.index! }));
      } else {
        pushSlug(slug);
      }
      if (!page) {
        setMissingPath(pathForPreviewSlug(slug, homeSlug));
        return;
      }
      setMissingPath(null);
    },
    [homeSlug, orderedPages, pushSlug],
  );

  const handlePreviewHref = useCallback(
    (href: string) => {
      const parsed = parsePreviewHref(href, homeSlug);
      if (parsed.kind === "hash" || parsed.kind === "ignore") return;
      if (parsed.kind === "external") {
        window.open(parsed.url, "_blank", "noopener,noreferrer");
        return;
      }
      navigateTo(parsed.slug);
    },
    [homeSlug, navigateTo],
  );

  useEffect(() => {
    if (!open) return;
    function onMessage(event: MessageEvent) {
      if (!isPreviewNavMessage(event.data)) return;
      handlePreviewHref(event.data.href);
    }
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, [handlePreviewHref, open]);

  const canBack = nav.index > 0;
  const canForward = nav.index < nav.stack.length - 1;
  const confirming = confirm?.confirming === true;

  function goBack() {
    if (!canBack) return;
    const nextIndex = nav.index - 1;
    const slug = nav.stack[nextIndex];
    if (!slug) return;
    navigateTo(slug, { index: nextIndex });
  }

  function goForward() {
    if (!canForward) return;
    const nextIndex = nav.index + 1;
    const slug = nav.stack[nextIndex];
    if (!slug) return;
    navigateTo(slug, { index: nextIndex });
  }

  function submitUrl(event: FormEvent) {
    event.preventDefault();
    const value = urlDraft.trim() || "/";
    handlePreviewHref(value.startsWith("/") || /^[a-z]+:/i.test(value) ? value : `/${value}`);
    setTreeOpen(false);
  }

  function selectPageFromTree(slug: string) {
    navigateTo(slug);
    setTreeOpen(false);
  }

  const stageBusy = loading || Boolean(error) || orderedPages.length === 0;

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (confirming) return;
        onOpenChange(next);
      }}
    >
      <DialogContent
        className="fixed inset-0 top-0 left-0 flex h-svh max-h-svh w-screen max-w-none translate-x-0 translate-y-0 flex-col gap-0 overflow-hidden rounded-none p-0 sm:max-w-none data-open:zoom-in-100 data-closed:zoom-out-100"
        showCloseButton={false}
      >
        <DialogHeader className="shrink-0 space-y-2 border-b border-border px-4 py-3 text-left">
          <div className="flex flex-wrap items-center gap-2">
            {versionNav ? (
              <div className="flex items-center gap-0.5">
                <IconButton
                  label="上一版"
                  disabled={confirming || !versionNav.hasPrevious}
                  onClick={versionNav.onPrevious}
                >
                  <ChevronLeft className="size-4" />
                </IconButton>
                <IconButton
                  label="下一版"
                  disabled={confirming || !versionNav.hasNext}
                  onClick={versionNav.onNext}
                >
                  <ChevronRight className="size-4" />
                </IconButton>
              </div>
            ) : null}
            <DialogTitle className="flex min-w-0 flex-1 flex-wrap items-baseline gap-x-2 gap-y-1">
              {title}
            </DialogTitle>
            <div className="flex shrink-0 flex-wrap items-center justify-end gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={confirming}
                onClick={() => onOpenChange(false)}
              >
                取消
              </Button>
              {confirm ? (
                <Button
                  type="button"
                  size="sm"
                  variant={confirm.tone === "destructive" ? "destructive" : "default"}
                  disabled={confirming || loading || Boolean(error)}
                  onClick={() => void confirm.onConfirm()}
                >
                  {confirming ? "回滚中…" : confirm.label}
                </Button>
              ) : null}
            </div>
          </div>
          {description ? (
            <DialogDescription asChild>
              <div>{description}</div>
            </DialogDescription>
          ) : null}
          {compareToggle ? (
            <div className="flex flex-wrap gap-2 pt-1" role="tablist" aria-label="对照版本">
              <ToggleChip
                pressed={compareToggle.side === "current"}
                disabled={confirming}
                onClick={() => compareToggle.onSideChange("current")}
                label={`当前正式版 · ${compareToggle.currentLabel}`}
              />
              <ToggleChip
                pressed={compareToggle.side === "target"}
                disabled={confirming}
                onClick={() => compareToggle.onSideChange("target")}
                label={`目标版 · ${compareToggle.targetLabel}`}
              />
            </div>
          ) : null}
        </DialogHeader>

        <div className="flex min-h-0 flex-1 flex-col overflow-hidden bg-background">
          <div className="flex shrink-0 items-center gap-1 border-b border-border bg-muted/40 px-1.5 py-1">
            <IconButton label="后退" disabled={confirming || !canBack || stageBusy} onClick={goBack}>
              <ArrowLeft className="size-3.5" />
            </IconButton>
            <IconButton
              label="前进"
              disabled={confirming || !canForward || stageBusy}
              onClick={goForward}
            >
              <ArrowRight className="size-3.5" />
            </IconButton>
            <IconButton
              label="首页"
              disabled={confirming || stageBusy}
              onClick={() => navigateTo(homeSlug)}
            >
              <Home className="size-3.5" />
            </IconButton>
            <IconButton
              label="刷新预览"
              disabled={confirming || stageBusy}
              onClick={() => setReloadToken((token) => token + 1)}
            >
              <RotateCw className="size-3.5" />
            </IconButton>
            <Popover open={treeOpen} onOpenChange={setTreeOpen} modal={false}>
              <PopoverAnchor asChild>
                <form onSubmit={submitUrl} className="min-w-0 flex-1">
                  <input
                    ref={urlInputRef}
                    value={urlDraft}
                    disabled={confirming || stageBusy}
                    onChange={(event) => setUrlDraft(event.target.value)}
                    onFocus={() => setTreeOpen(true)}
                    onClick={() => setTreeOpen(true)}
                    onKeyDown={(event) => {
                      if (event.key === "Escape") {
                        setUrlDraft(displayPath);
                        setTreeOpen(false);
                      }
                    }}
                    aria-label="预览地址"
                    role="combobox"
                    aria-autocomplete="list"
                    aria-expanded={treeOpen}
                    aria-controls={BRAND_SITE_PAGE_TREE_ID}
                    autoComplete="off"
                    spellCheck={false}
                    className="h-7 w-full rounded-md border border-border bg-background px-2 font-mono text-xs text-foreground outline-none focus:border-primary disabled:opacity-50"
                  />
                </form>
              </PopoverAnchor>
              <PopoverContent
                align="start"
                side="bottom"
                sideOffset={4}
                className="w-[var(--radix-popover-trigger-width)] max-w-none rounded-lg p-0"
                onOpenAutoFocus={(event) => event.preventDefault()}
                onCloseAutoFocus={(event) => event.preventDefault()}
                onInteractOutside={(event) => {
                  if (urlInputRef.current?.contains(event.target as Node)) {
                    event.preventDefault();
                  }
                }}
              >
                <BrandSitePageTree
                  pages={treePages}
                  homeSlug={homeSlug}
                  activeSlug={viewingPage?.slug ?? viewingSlug}
                  missingPath={missingPath}
                  query={addressBarTreeQuery(urlDraft, displayPath)}
                  onNavigate={selectPageFromTree}
                />
              </PopoverContent>
            </Popover>
            <div className="flex items-center gap-0.5 rounded-md border border-border p-0.5">
              <IconButton
                label="桌面预览"
                pressed={viewport === "desktop"}
                disabled={confirming}
                onClick={() => setViewport("desktop")}
              >
                <Monitor className="size-3.5" />
              </IconButton>
              <IconButton
                label="手机预览"
                pressed={viewport === "mobile"}
                disabled={confirming}
                onClick={() => setViewport("mobile")}
              >
                <Smartphone className="size-3.5" />
              </IconButton>
            </div>
          </div>

          <div className="relative flex min-h-0 flex-1 items-stretch justify-center bg-neutral-200/70">
            {loading ? (
              <p className="self-center px-4 text-sm text-muted-foreground">加载预览…</p>
            ) : error ? (
              <p className="self-center px-4 text-sm text-destructive">{error}</p>
            ) : orderedPages.length === 0 ? (
              <p className="self-center px-4 text-sm text-muted-foreground">这一版没有可预览的页面。</p>
            ) : (
              <iframe
                key={previewIdentityKey}
                title={viewingPage?.title || "正式版预览"}
                sandbox="allow-scripts"
                srcDoc={srcDoc}
                className={cn(
                  "h-full bg-white",
                  viewport === "mobile" ? "mx-auto w-[390px] max-w-full shadow-lg" : "w-full",
                )}
              />
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function ToggleChip({
  pressed,
  disabled,
  onClick,
  label,
}: {
  pressed: boolean;
  disabled?: boolean;
  onClick: () => void;
  label: string;
}) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={pressed}
      disabled={disabled}
      onClick={onClick}
      className={cn(
        "rounded-md border px-3 py-1.5 text-sm transition-colors",
        pressed
          ? "border-primary bg-primary/10 text-foreground"
          : "border-border bg-background text-muted-foreground hover:bg-muted",
        disabled && "pointer-events-none opacity-50",
      )}
    >
      {label}
    </button>
  );
}

function IconButton({
  label,
  disabled,
  pressed,
  onClick,
  children,
}: {
  label: string;
  disabled?: boolean;
  pressed?: boolean;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      aria-pressed={pressed}
      title={label}
      disabled={disabled}
      onClick={onClick}
      className={cn(
        "rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground disabled:pointer-events-none disabled:opacity-30",
        pressed && "bg-muted text-foreground",
      )}
    >
      {children}
    </button>
  );
}
